/**
 * Core-100 R2 — CORE-BC-GAP-006: which recommendations are worth making. Acceptance per kind over
 * the last 30 days, counted by the server (`recommendation_acceptance`, owner/manager). A rate is
 * over DECIDED offers only, and absent when nothing was decided — never 0% from silence.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

export interface AcceptanceRow {
  kind: string; offered: number; accepted: number; dismissed: number; undecided: number; acceptance_rate: number | null;
}

const KIND_LABEL: Record<string, string> = {
  optimizer_suggestion: 'Schedule suggestions',
  addon_suggestion: 'Add-on suggestions',
  assignment_top_pick: 'Top-ranked technician kept',
};

export function describeAcceptance(r: AcceptanceRow): string {
  const label = KIND_LABEL[r.kind] ?? r.kind;
  if (r.offered === 0) return `${label}: none in this period`;
  const rate = r.acceptance_rate === null ? 'no decisions yet' : `${Math.round(r.acceptance_rate * 100)}% accepted of those decided`;
  return `${label}: ${r.offered} offered · ${r.accepted} accepted · ${r.dismissed} ${r.kind === 'assignment_top_pick' ? 'changed' : 'dismissed'}${r.undecided > 0 ? ` · ${r.undecided} undecided` : ''} — ${rate}`;
}

/**
 * Core-100 R4 — CORE-BC GAP-005: override evidence, READ. A manual cross-technician move asks the person why; the
 * reasons are counted by the server (`assignment_override_reasons`, owner/manager) so a shop can see where the
 * recommendation is consistently set aside, and for what. Overrides recorded before reasons were stored are counted
 * as "reason not recorded", never guessed.
 */
export interface OverrideReasonRow { reason: string; is_learning_signal: boolean; overrides: number; share: number }

const REASON_LABEL: Record<string, string> = {
  skill_match: 'Skill match', tech_preference: 'Technician preference', customer_request: 'Customer request',
  specialty_match: 'Specialty match', capacity_balance: 'Capacity balance', tech_absent: 'Technician absent',
  shift_conflict: 'Shift conflict', break_conflict: 'Break conflict', bay_closure: 'Bay closure',
  weather_block: 'Weather', other: 'Other', not_recorded: 'Reason not recorded',
};

export function describeOverrideReason(r: OverrideReasonRow): string {
  const label = REASON_LABEL[r.reason] ?? r.reason.replace(/_/g, ' ');
  return `${label}: ${r.overrides} (${Math.round(r.share * 100)}%)${r.is_learning_signal ? ' — learned from' : ''}`;
}

export function RecommendationAcceptanceCard({ shopId }: { shopId: string }) {
  const reasons = useQuery({
    queryKey: ['assignment-override-reasons', shopId],
    queryFn: async (): Promise<OverrideReasonRow[]> => {
      const { data, error } = await supabase.rpc('assignment_override_reasons' as never, { p_shop_id: shopId, p_days: 30 } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as OverrideReasonRow[];
    },
  });
  const q = useQuery({
    queryKey: ['recommendation-acceptance', shopId],
    queryFn: async (): Promise<AcceptanceRow[]> => {
      const { data, error } = await supabase.rpc('recommendation_acceptance' as never, { p_shop_id: shopId, p_days: 30 } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as AcceptanceRow[];
    },
  });
  return (
    <Card data-testid="recommendation-acceptance">
      <CardHeader>
        <CardTitle className="text-base">Recommendation acceptance (30 days)</CardTitle>
        <CardDescription>How often OptiBay's recommendations were taken — so the useful ones can be told from the noise.</CardDescription>
      </CardHeader>
      <CardContent className="text-sm space-y-1">
        {q.isError && <p className="text-muted-foreground">Acceptance could not be read.</p>}
        {q.data?.map((r) => <div key={r.kind}>{describeAcceptance(r)}</div>)}
        <div className="pt-2" data-testid="override-reasons">
          <p className="text-xs font-medium text-muted-foreground">Why work was moved to a different technician</p>
          {reasons.isError && <p className="text-muted-foreground">Override reasons could not be read.</p>}
          {reasons.data && reasons.data.length === 0 && <p className="text-muted-foreground">No technician was overridden in this period.</p>}
          {reasons.data?.map((r) => <div key={r.reason}>{describeOverrideReason(r)}</div>)}
        </div>
      </CardContent>
    </Card>
  );
}
